import React, { FC, Fragment, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { Formik, Form as FormikForm } from 'formik';
import { cloneDeep, compact, findIndex, isEqual } from 'lodash';
import { useHistory } from 'react-router';

import {
  ContentInput,
  ManualPostMutation,
  Network,
  PostType,
  SocialAccount,
  useManualPostMutation,
} from 'api';
import { useFollowed, useNotify } from 'hooks';
import { ContentProvider, SelectedNetworksProvider } from 'providers';
import { IWithSocialConnections, withSocialConnections } from 'providers/queries';

import { SocialNetwork } from '../components/widgets';
import { formatBody, formatDisclosures } from '../../posting/helpers';
import { PostSchema } from '../../posting/validations';
import { ShareDialog } from '../../sharing';
import Post from './Post';
import { FACEBOOK_ID } from '../../posting/constants';
import { FormikEffect } from '../../../components';

const PostCreate: FC<IWithSocialConnections> = ({ connections }) => {
  const notify = useNotify();
  const history = useHistory();
  const { postable } = useFollowed();

  const [content, setContent] = useState({});
  const [open, setOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [socialNetwork, setSocialNetwork] = useState(false);
  const [selectedNetworks, setSelectedNetworks] = useState(getSelectedNetworks());

  const { list, hasTimeline } = postable;
  const groupIds = getGroupIds();

  function getGroupIds() {
    const length = hasTimeline ? 2 : 1;
    return list.slice(0, length).map(item => item && item.id);
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const url = searchParams.get('share-url');

    if (shareUrl) {
      setShareUrl(url);
    }
  }, []);

  const [processContent] = useManualPostMutation({
    onError: handleError,
    onCompleted,
  });

  function getValues(): ContentInput {
    return {
      group_ids: groupIds,
      content_source_id: 'manual',
      is_engageable: false,
      is_important: false,
      is_shareable: true,
      is_pinned: false,
      pinned_end_at: null,
      network_ids: [],
      lock_commentary: false,
      content_object: {
        post_type: PostType.Manual,
        body: shareUrl || '',
        url: shareUrl,
        title: '',
      },
      commentary: [
        { network: Network.Twitter, commentary: '' },
        { network: Network.Facebook, commentary: '' },
        { network: Network.Linkedin, commentary: '' },
      ],
      url_param_values: [],
      disclosures: [
        { network: Network.Facebook, disclosure_id: '' },
        { network: Network.Twitter, disclosure_id: '' },
        { network: Network.Linkedin, disclosure_id: '' },
      ],
    };
  }

  function getLeadingAccount(filteredConnections: SocialAccount[], provider: Network) {
    return filteredConnections.find(conn => conn.provider === provider);
  }

  function getSelectedNetworks() {
    const filterExpired = connections.filter(connection => !connection.expires_soon);
    const twitterAccount = getLeadingAccount(filterExpired, Network.Twitter);
    const linkedinAccount = getLeadingAccount(filterExpired, Network.Linkedin);
    const initialSelectedNetworks: Array<SocialAccount | string> = compact(
      [twitterAccount].concat(linkedinAccount),
    );

    return initialSelectedNetworks.concat(FACEBOOK_ID);
  }

  function setShareSocialNetwork(isChecked: boolean) {
    setSocialNetwork(isChecked);
  }

  function toggleNetwork(account: SocialAccount | string) {
    const nextNetworks = cloneDeep(selectedNetworks);
    const index = nextNetworks.findIndex(selectedNetwork => isEqual(selectedNetwork, account));

    // Remove network
    if (index >= 0) {
      nextNetworks.splice(index, 1);
      return setSelectedNetworks(nextNetworks);
    }

    // Add Facebook
    if (typeof account === 'string') {
      nextNetworks.splice(0, 0, account);
      return setSelectedNetworks(nextNetworks);
    }

    // Add or toggle accounts for a given network
    const existingNetworkIndex = findIndex(nextNetworks, ['provider', account.provider]);
    if (existingNetworkIndex >= 0) {
      nextNetworks.splice(existingNetworkIndex, 1);
    }

    nextNetworks.splice(0, 0, account);
    setSelectedNetworks(nextNetworks);
  }

  function handleFormChange(values: ContentInput) {
    if (!values.is_shareable) {
      setSelectedNetworks([]);
    }
  }

  function onCompleted(mutationResult: ManualPostMutation) {
    if (!socialNetwork) {
      notify.info({ i18n: { i18nKey: 'posting:created' } });
      history.push('/');
    }

    setContent(mutationResult.processContent);
    setOpen(true);
  }

  function onSubmit(contentInput: ContentInput) {
    const contentToSubmit: ContentInput = cloneDeep(contentInput);

    contentToSubmit.group_ids = compact(contentToSubmit.group_ids);
    contentToSubmit.content_object.body = formatBody(contentToSubmit);
    contentToSubmit.commentary = contentToSubmit.commentary.filter(c => !!c.commentary);
    contentToSubmit.disclosures = formatDisclosures(contentToSubmit);

    delete contentToSubmit.content_object.url_metadata;
    delete contentToSubmit.content_object.tweet_metadata;
    delete contentToSubmit.content_object.youtube_metadata;
    delete contentToSubmit.content_object.instagram_metadata;
    delete contentToSubmit.content_object.reddit_metadata;
    delete contentToSubmit.content_object.metadata_keys;

    const options = {
      variables: { content: contentToSubmit },
    };

    return processContent(options);
  }

  function handleError() {
    notify.mutationError();
  }

  function renderSocialNetwork() {
    return (
      <Grid item xs container>
        <SocialNetwork setShareSocialNetwork={setShareSocialNetwork} />
      </Grid>
    );
  }

  return (
    <Fragment>
      <Formik onSubmit={onSubmit} initialValues={getValues()} validationSchema={PostSchema}>
        {() => (
          <FormikForm>
            <SelectedNetworksProvider value={{ selectedNetworks, toggleNetwork }}>
              <Post>{renderSocialNetwork()}</Post>

              <FormikEffect onChange={handleFormChange} delay={0} validate={false} />

              {content && (
                <ContentProvider value={{ content }}>
                  <ShareDialog onClose={() => setOpen(false)} open={open} />
                </ContentProvider>
              )}
            </SelectedNetworksProvider>
          </FormikForm>
        )}
      </Formik>
    </Fragment>
  );
};

export default withSocialConnections('none')<{}>(PostCreate);
